import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Product } from '../models/Product';

export type ShoppingListType = {
  id: number;
  name: string;
  price: number;
  quantity: number;
  slug: string;
  image: string;
  packaged: boolean;
  returnedQuantity?: number;  // Added this field to track returned items
  singleItemDeliveryCost: number;
};

interface ShoppingListContextType {
  shoppingList: ShoppingListType[];
  addToShoppingList: (product: Product, quantity: number) => void;
  removeProductFromShoppingList: (productId: number) => void;
  setProductQuantity: (productId: number, quantity: number) => void;
  isShoppingListOpen: boolean;
  setIsShoppingListOpen: (isOpen: boolean) => void;
  foreshadow: boolean;
  setForeshadow: (foreshadow: boolean) => void;
}

const ShoppingListContext = createContext<ShoppingListContextType | undefined>(
  undefined,
);

export const useShoppingList = () => {
  const context = useContext(ShoppingListContext);
  if (!context) {
    throw new Error(
      'useShoppingList must be used within a ShoppingListProvider',
    );
  }
  return context;
};

interface ShoppingListProviderProps {
  children: ReactNode;
}

export const ShoppingListProvider: React.FC<ShoppingListProviderProps> = ({
  children,
}) => {
  const [shoppingListObject, setShoppingListObject] = React.useState<
    ShoppingListType[]
  >([]);
  const [isShoppingListOpen, setIsShoppingListOpen] = useState(false);
  const [foreshadow, setForeshadow] = useState(false);

  const addToShoppingList = (product: Product, quantity: number) => {
    const shoppingList = localStorage.getItem('shoppingList');
    let updatedShoppingList: ShoppingListType[] = [];
    if (shoppingList) {
      updatedShoppingList = JSON.parse(shoppingList);
    }
    const productIndex = updatedShoppingList.findIndex(
      (p) => p.id === product.id,
    );

    if (productIndex !== -1) {
      updatedShoppingList[productIndex].quantity += quantity;
      localStorage.setItem('shoppingList', JSON.stringify(updatedShoppingList));
      setShoppingListObject(updatedShoppingList);
      return;
    }

    updatedShoppingList.push({
      id: product.id,
      name: product.name,
      slug: product.slug,
      price: product.price,
      quantity: quantity,
      image: product.mobilePrimaryImage,
      packaged: product.packaged,
      singleItemDeliveryCost: product.singleItemDeliveryCost || 0,
    });
    localStorage.setItem('shoppingList', JSON.stringify(updatedShoppingList));
    setShoppingListObject(updatedShoppingList);
  };

  const removeProductFromShoppingList = (productId: number) => {
    const shoppingList = localStorage.getItem('shoppingList');
    let updatedShoppingList: ShoppingListType[] = [];
    if (shoppingList) {
      updatedShoppingList = JSON.parse(shoppingList);
    }
    const productIndex = updatedShoppingList.findIndex(
      (product) => product.id === productId,
    );
    updatedShoppingList.splice(productIndex, 1);
    localStorage.setItem('shoppingList', JSON.stringify(updatedShoppingList));
    setShoppingListObject(updatedShoppingList);
  };

  const getShoppingList = useCallback(() => {
    const shoppingList = localStorage.getItem('shoppingList');
    if (shoppingList) {
      JSON.parse(shoppingList);
      setShoppingListObject(JSON.parse(shoppingList));
    }
  }, []);

  const setProductQuantity = (productId: number, quantity: number) => {
    const shoppingList = localStorage.getItem('shoppingList');
    let updatedShoppingList: ShoppingListType[] = [];
    if (shoppingList) {
      updatedShoppingList = JSON.parse(shoppingList);
    }
    const productIndex = updatedShoppingList.findIndex(
      (product) => product.id === productId,
    );
    updatedShoppingList[productIndex].quantity = quantity;
    localStorage.setItem('shoppingList', JSON.stringify(updatedShoppingList));
    setShoppingListObject(updatedShoppingList);
  };

  useEffect(() => {
    getShoppingList();
  }, [getShoppingList]);

  return (
    <ShoppingListContext.Provider
      value={{
        shoppingList: shoppingListObject,
        addToShoppingList,
        removeProductFromShoppingList,
        setProductQuantity,
        isShoppingListOpen,
        setIsShoppingListOpen,
        foreshadow,
        setForeshadow,
      }}
    >
      {children}
    </ShoppingListContext.Provider>
  );
};
